import type { Stripe } from '@stripe/stripe-js';

// Initialize Stripe with publishable key
const stripePublishableKey = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY;

// True lazy load - only imports @stripe/stripe-js when first needed (~500KB savings)
let stripePromise: Promise<Stripe | null> | null = null;

export const getStripe = () => {
  if (!stripePromise) {
    if (!stripePublishableKey) {
      console.warn('Stripe publishable key not configured');
      return Promise.resolve(null);
    }
    // Dynamic import - Stripe SDK only loads when this function is called
    stripePromise = import('@stripe/stripe-js').then(({ loadStripe }) =>
      loadStripe(stripePublishableKey)
    );
  }
  return stripePromise;
};

/**
 * Create a checkout session and redirect to Stripe Checkout
 */
export const redirectToCheckout = async (sessionId: string) => {
  const stripe = await getStripe();
  
  if (!stripe) {
    throw new Error('Stripe is not configured');
  }

  const { error } = await stripe.redirectToCheckout({ sessionId });
  
  if (error) {
    console.error('Stripe checkout error:', error);
    throw error;
  }
};

/**
 * Create a billing portal session and redirect
 */
export const redirectToBillingPortal = async (portalUrl: string) => {
  window.location.href = portalUrl;
};

/**
 * Format amount from cents to dollars
 */
export const formatAmount = (amountInCents: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  }).format(amountInCents / 100);
};

/**
 * Format date for display
 */
export const formatDate = (dateString: string): string => {
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }).format(new Date(dateString));
};

/**
 * Get Stripe dashboard URL for a customer
 */
export const getStripeDashboardUrl = (customerId: string): string => {
  const isTestMode = stripePublishableKey?.startsWith('pk_test_');
  const baseUrl = 'https://dashboard.stripe.com';
  const mode = isTestMode ? 'test' : '';
  return `${baseUrl}/${mode}/customers/${customerId}`;
};